import type { HealthCheckResponse, StatsResponse } from '@/types/api'
import { apiDelete, apiGet, apiPost, buildUrl } from './http'

// API système et utilitaires
export const systemApi = {
  /**
   * Vérifier l'état de santé de l'API
   */
  async healthCheck(): Promise<HealthCheckResponse> {
    console.log('[SystemAPI] healthCheck')
    return apiGet<HealthCheckResponse>('/health')
  },

  /**
   * Récupérer les statistiques générales de la plateforme
   */
  async getStats(): Promise<StatsResponse> {
    console.log('[SystemAPI] getStats')
    return apiGet<StatsResponse>('/stats')
  },

  /**
   * Tester la connectivité avec l'API
   */
  async ping(): Promise<{ success: boolean; timestamp: string }> {
    console.log('[SystemAPI] ping')
    try {
      const response = await this.healthCheck()
      return {
        success: response.status === 'ok',
        timestamp: response.timestamp,
      }
    } catch (error) {
      console.error('[SystemAPI] ping failed:', error)
      return {
        success: false,
        timestamp: new Date().toISOString(),
      }
    }
  },

  /**
   * Liste des backups
   */
  async getBackups(): Promise<{ data: Backup[] }> {
    console.log('[SystemAPI] getBackups')
    return apiGet<{ data: Backup[] }>('/system/backups')
  },

  /**
   * Créer un backup manuel
   */
  async createBackup(): Promise<{ data: Backup }> {
    console.log('[SystemAPI] createBackup')
    return apiPost<{ data: Backup }>('/system/backups', {})
  },

  /**
   * Télécharger un backup
   */
  async downloadBackup(id: number): Promise<Blob> {
    console.log('[SystemAPI] downloadBackup:', id)
    const url = buildUrl(`/system/backups/${id}/download`)
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
    if (!response.ok) {
      throw new Error('Erreur lors du téléchargement du backup')
    }
    return response.blob()
  },

  /**
   * Restaurer un backup
   */
  async restoreBackup(
    id: number,
    force = false
  ): Promise<{ success: boolean; message: string; data?: { warning: string | null } }> {
    console.log('[SystemAPI] restoreBackup:', id, force)
    return apiPost<{ success: boolean; message: string; data?: { warning: string | null } }>(
      `/system/backups/${id}/restore`,
      { force }
    )
  },

  /**
   * Supprimer un backup
   */
  async deleteBackup(id: number): Promise<{ success: boolean; message: string }> {
    console.log('[SystemAPI] deleteBackup:', id)
    return apiDelete<{ success: boolean; message: string }>(`/system/backups/${id}`)
  },

  /**
   * Statistiques des backups
   */
  async getBackupStats(): Promise<{ data: BackupStats }> {
    console.log('[SystemAPI] getBackupStats')
    return apiGet<{ data: BackupStats }>('/system/backups/stats')
  },

  /**
   * Vérifier la compatibilité des migrations
   */
  async checkBackupCompatibility(id: number): Promise<{ data: BackupCompatibility }> {
    console.log('[SystemAPI] checkBackupCompatibility:', id)
    return apiGet<{ data: BackupCompatibility }>(`/system/backups/${id}/compatibility`)
  },
}

// Types pour les backups
export interface Backup {
  id: number
  filename: string
  type: 'auto' | 'manual'
  createdAt: string
  expiresAt: string | null
  size: number
  migrationsVersion: string | null
  status: 'pending' | 'completed' | 'failed'
  errorMessage: string | null
}

export interface BackupStats {
  total: number
  auto: number
  manual: number
  totalSize: number
  expired: number
}

export interface BackupCompatibility {
  compatible: boolean
  backupVersion: string | null
  currentVersion: string | null
  warning: string | null
}
